import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { getTransformCowsFeedGroups } from '../../utils'
import cloneDeep from 'lodash.clonedeep'
import { FormFields } from '~/components/FormFields'
import { StyledDivider } from '../../../style'
import styled from 'styled-components'
import { CowsCoarseFeed } from '~/api/services/co2'
import FeedSlider from './FeedSlider'

const Title = styled.div`
font-size: 15px !important;
font-weight: bold;
`

interface Props {
  silageCoarseFeeds: CowsCoarseFeed[];
  silageConcentratedFeeds: CowsCoarseFeed[];
}

const ForageFeed: React.FC<Props> = ({ silageCoarseFeeds, silageConcentratedFeeds }) => {
  const { control, getValues, setValue } = useFormContext()

  const rearingPhases = useWatch({ control, name: 'rearingPhases' })
  const feedGroups = useWatch({ control, name: 'feedGroups' })
  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })

  function getRearingParsedData(data) {
    const result = {
      coarseFeeds: [],
      concentratedFeeds: []
    };

    data.forEach(item => {
      item.concentratedFeeds.forEach(feed => {
        const isPushedFeed = Boolean(result.concentratedFeeds.find(item => item.id === feed.id))
        const isInSilageOptions = Boolean(silageConcentratedFeeds.find(item => item.id === feed.id))
        if (!isPushedFeed && isInSilageOptions) {
          const concentratedFeeds = getValues('calvesConcentratedFeeds')
          const index = concentratedFeeds?.findIndex(concentratedFeed => concentratedFeed.id === feed.id)
          result.concentratedFeeds.push({
            ...feed,
            index
          });
        }
      });
      item.coarseFeeds.forEach(feed => {
        const isPushedFeed = Boolean(result.coarseFeeds.find(item => item.id === feed.id))
        const isInSilageOptions = Boolean(silageCoarseFeeds.find(item => item.id === feed.id))
        if (!isPushedFeed && isInSilageOptions) {
          const coarseFeeds = getValues('calvesCoarseFeeds')
          const index = coarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
          result.coarseFeeds.push({
            ...feed,
            index
          });
        }
      });
    });
    return result
  }

  function getCowParsedData(data) {
    const result = {
      coarseFeeds: [],
      concentratedFeeds: [],
      juicyFeeds: []
    };

    if (watchPhaseEnabled) {
      data = getTransformCowsFeedGroups(data)
    }

    data.forEach(item => {
      item.concentratedFeeds.forEach(feed => {
        const isPushedFeed = Boolean(result.concentratedFeeds.find(item => item.id === feed.id))
        const isInSilageOptions = Boolean(silageConcentratedFeeds.find(item => item.id === feed.id))
        if (!isPushedFeed && isInSilageOptions) {
          const concentratedFeeds = getValues('cowsConcentratedFeeds')
          const index = concentratedFeeds?.findIndex(concentratedFeed => concentratedFeed.id === feed.id)
          result.concentratedFeeds.push({
            ...feed,
            index
          });
        }
      });
      item.juicyFeeds.forEach(feed => {
        if (!result.juicyFeeds.find(item => item.id === feed.id)) {
          const juicyFeeds = getValues('juicyFeeds')
          const index = juicyFeeds?.findIndex(juicyFeed => juicyFeed.id === feed.id)
          result.juicyFeeds.push({
            ...feed,
            dryMatter: juicyFeeds[index]?.dryMatter,
            energy: juicyFeeds[index]?.energy, index,
            juicyFeedId: juicyFeeds[index].juicyFeedId
          });
        }
      });
      item.coarseFeeds.forEach(feed => {
        const isPushedFeed = Boolean(result.coarseFeeds.find(item => item.id === feed.id))
        const isInSilageOptions = Boolean(silageCoarseFeeds.find(item => item.id === feed.id))
        if (!isPushedFeed && isInSilageOptions) {
          const coarseFeeds = getValues('cowsCoarseFeeds')
          const index = coarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
          result.coarseFeeds.push({
            ...feed,
            index
          });
        }
      });
    });

    return result
  }

  function renderCowsFeedsPercentageInputs() {
    const cowsConcentratedFeeds = getValues('cowsConcentratedFeeds')
    const cowsCoarseFeeds = getValues('cowsCoarseFeeds')
    const juicyFeeds = getValues('juicyFeeds')
    return <>
      {cowsFeeds.coarseFeeds.length > 0 && <div style={{ marginBottom: '2em' }}>
        <Title>{'Milchkuh / Grobfutter'}</Title>
        <StyledDivider />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
          {cowsFeeds.coarseFeeds.map(feed => {
            const index = cowsCoarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
            const name = `cowsCoarseFeeds[${index}].homegrownPercentage`
            return <FeedSlider
              key={feed.id}
              name={name}
              label={feed.label}
            />
          })}
        </div>
      </div>}
      {cowsFeeds.concentratedFeeds.length > 0 && <div style={{ marginBottom: '2em' }}>
        <Title>{'Milchkuh / Kraft- und Mineralfutter'}</Title>
        <StyledDivider />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
          {cowsFeeds.concentratedFeeds.map(feed => {
            const index = cowsConcentratedFeeds?.findIndex(concetratedFeed => concetratedFeed.id === feed.id)
            const name = `cowsConcentratedFeeds[${index}].homegrownPercentage`
            return <FeedSlider
              key={feed.id}
              name={name}
              label={`${feed.groupLabel}-${feed.label}`}
            />
          })}
        </div>
      </div>}
      {cowsFeeds.juicyFeeds.length > 0 && <div style={{ marginBottom: '2em' }}>
        <Title>{'Milchkuh / Saftfutter'}</Title>
        <StyledDivider />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
          {cowsFeeds.juicyFeeds.map(feed => {
            const index = juicyFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
            const name = `juicyFeeds[${index}].homegrownPercentage`
            return <FeedSlider
              key={feed.id}
              name={name}
              label={feed.label}
            />
          })}
        </div>
      </div>}
    </>
  }

  function renderRearingPercentageInputs() {
    const calvesCoarseFeeds = getValues('calvesCoarseFeeds')
    const calvesConcentratedFeeds = getValues('calvesConcentratedFeeds')
    return <>
      {rearingPhasesFeeds.coarseFeeds.length > 0 && <div style={{ marginBottom: '2em' }}>
        <Title>{'Färse / Grobfutter'}</Title>
        <StyledDivider />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
          {rearingPhasesFeeds.coarseFeeds.map(feed => {
            const index = calvesCoarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
            const name = `calvesCoarseFeeds[${index}].homegrownPercentage`
            return <FeedSlider
              key={feed.id}
              name={name}
              label={feed.label}
            />
          })}
        </div>
      </div>}
      {rearingPhasesFeeds.concentratedFeeds.length > 0 && <div style={{ marginBottom: '2em' }}>
        <Title>{'Färse / Kraft- und Mineralfutter'}</Title>
        <StyledDivider />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
          {rearingPhasesFeeds.concentratedFeeds.map(feed => {
            const index = calvesConcentratedFeeds?.findIndex(concetratedFeed => concetratedFeed.id === feed.id)
            const name = `calvesConcentratedFeeds[${index}].homegrownPercentage`
            return <FeedSlider
              key={feed.id}
              name={name}
              label={`${feed.groupLabel}-${feed.label}`}
            />
          })}
        </div>
      </div>}
    </>
  }

  const rearingPhasesFeeds = getRearingParsedData(cloneDeep(rearingPhases))
  const cowsFeeds = getCowParsedData(cloneDeep(feedGroups))

  return <>
    {renderCowsFeedsPercentageInputs()}
    {renderRearingPercentageInputs()}
  </>
}
export default ForageFeed